import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { useInView } from 'react-cool-inview';
import classnames from 'classnames';

import { Button, Image, Link } from 'components/ui';
import { isArrayEmpty } from 'helpers/ArrayHelpers';
import { getDocumentToReactComponentsOptions } from 'helpers/LandingPageHelpers';
import { parseAbsoluteUrl } from 'helpers/UrlHelpers';
import { useAnalytics } from 'hooks/useAnalytics';

import type { IContentfulImage, IContentfulRichText, Nullable } from 'types';

import styles from './AlphaTemplateBoxesBlock.module.scss';

interface Props {
  boxes: any[],
  hasGrayBackground?: boolean,
  hasPatternBackground?: boolean,
  isMobilePreview?: boolean
}

const AlphaTemplateBoxesBlock = (props: Props) => {

  const {
    boxes = [], hasGrayBackground, hasPatternBackground, isMobilePreview
  } = props;

  if (isArrayEmpty(boxes, true)) return null;

  return (
    <div className={classnames(styles.root, { [styles.gray]: hasGrayBackground }, { [styles.pattern]: hasPatternBackground })}>
      <div className={styles.inner}>
        <div className={styles.boxes}>
          {boxes.map((box, index) => {
            return (
              <Box key={index} isMobilePreview={isMobilePreview} {...box} />
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface BoxProps {
  button: {
    link?: string,
    type?: string,
    text?: string,
    analyticsId?: string
  },
  title?: string,
  description: Nullable<IContentfulRichText>,
  disclaimers: string[],
  image: IContentfulImage,
  isMobilePreview?: boolean,
  type: string
}

const Box = (props: BoxProps) => {
  const {
    button, description, disclaimers = [], image, title, type,
    isMobilePreview
  } = props;

  const { json: descriptionJson } = description || {};
  const { url } = image || {};

  const options = React.useMemo(() => getDocumentToReactComponentsOptions({ isMobilePreview }), [isMobilePreview]);

  return (
    <div className={classnames(styles.box, styles[type])}>
      <div className={styles.content}>
        {
          url && (
            <div className={styles.image}>
              <Image
                optimize
                src={url}
                height={74}
                width={74}
              />
            </div>
          )
        }
        {
          title && (
            <div className={styles.title}>{title}</div>
          )
        }
        {
          description && (
            <div className={styles.description}>{documentToReactComponents(descriptionJson, options)}</div>
          )
        }
        {
          button && (
            <BoxButton button={button} isMobilePreview={isMobilePreview} />
          )
        }
        {
          disclaimers?.length > 0 && (
            <div className={styles.disclaimers}>
              {disclaimers?.map((disclaimer: any, index: number) => (
                <div key={index} className={styles.disclaimer}>
                  {`${'*'.repeat(index + 1)}${disclaimer}`}
                </div>
              ))}
            </div>
          )
        }
      </div>
    </div>
  );
};

interface BoxButtonProps {
  button: BoxProps['button'],
  isMobilePreview?: boolean
}

const BoxButton = (props: BoxButtonProps) => {
  const {
    button,
    isMobilePreview
  } = props;

  const {
    link: buttonLink,
    type: buttonType,
    text: buttonText,
    analyticsId: buttonAnalyticsId
  } = button || {};

  // Hooks

  const analytics = useAnalytics();

  const { observe } = useInView({
    threshold: 0.75,
    onEnter: ({ unobserve }) => {
      handleEnter();
      unobserve();
    }
  });

  // Handlers

  const handleEnter = () => {
    if (buttonAnalyticsId) {
      analytics.viewLandingPageCTA(buttonAnalyticsId);
    }
  };

  const handleClick = () => {
    if (buttonAnalyticsId) {
      analytics.clickLandingPageCTA(buttonAnalyticsId);
    }
  };

  // Props

  const parsedUrl = React.useMemo(() => (buttonLink ? parseAbsoluteUrl(buttonLink) : null), [buttonLink]);
  const {
    isInternal,
    mobilePreviewPathname,
    pathname
  } = parsedUrl || {};

  const href = isMobilePreview ? mobilePreviewPathname ? mobilePreviewPathname.toString() : pathname : pathname;

  // Render Internal Link

  if (href && isInternal) {
    return (
      <Link
        passHref
        href={href}
      >
        <Button
          ref={observe}
          className={styles.button}
          tag="a"
          variant={buttonType as any}
          onClick={handleClick}
        >
          {buttonText}
        </Button>
      </Link>
    );
  }

  // Render External Link

  return (
    <Button
      ref={observe}
      className={styles.button}
      rel="noreferrer"
      tag="a"
      href={buttonLink}
      target={isMobilePreview ? '_self' : '_blank'}
      variant={buttonType as any}
      onClick={handleClick}
    >
      {buttonText}
    </Button>
  );
};

export default AlphaTemplateBoxesBlock;
